// tabs/IntrariTab.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  Checkbox,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CircularProgress from "@mui/material/CircularProgress";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const DateRangeInput = React.forwardRef(function DateRangeInput(
  { value, onClick, placeholder },
  ref
) {
  return (
    <TextField
      fullWidth
      variant="outlined"
      label="Interval de date"
      value={value}
      onClick={onClick}
      inputRef={ref}
      placeholder={placeholder}
      InputProps={{ readOnly: true }}
    />
  );
});

const startOfDay = (date) => {
  if (!date) return null;
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  if (!date) return null;
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const QUICK_RANGE_PRESETS = [
  { label: "Astăzi", value: "today" },
  { label: "Ultimele 7 zile", value: "last7" },
  { label: "Ultimele 30 zile", value: "last30" },
  { label: "Luna curentă", value: "thisMonth" },
];

const TABLE_COLUMNS = [
  { id: "rec", label: "REC" },
  { id: "nrJurnalSpecial", label: "Nr Jurnal" },
  { id: "sku", label: "SKU" },
  { id: "tipContract", label: "Tip Contract" },
  { id: "numeSiPrenume", label: "Nume" },
  { id: "summary", label: "Summary" },
  { id: "pretIntrareRON", label: "Preț Intrare", align: "right" },
  { id: "dataIntrare", label: "Data Intrare" },
];

const COLUMN_VALUE_GETTERS = {
  rec: (row) => (row.rec === 1 || row.rec === "1" ? 1 : 0),
  nrJurnalSpecial: (row) => row.nrJurnalSpecial || "",
  sku: (row) => row.sku || "",
  tipContract: (row) => row.tipContract || "",
  numeSiPrenume: (row) => row.numeSiPrenume || "",
  summary: (row) => row.summary || "",
  pretIntrareRON: (row) =>
    row.pretIntrareRON !== null && row.pretIntrareRON !== undefined
      ? Number(row.pretIntrareRON)
      : null,
  dataIntrare: (row) =>
    row.dataIntrare ? new Date(row.dataIntrare).getTime() : null,
};

const stripDiacritics = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const toPdfSafeText = (value) => stripDiacritics(value).replace(/—/g, "-");

// Excel export function: preserves the current table ordering
const exportToExcel = (data) => {
  const XLSX = require("xlsx");

  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const formatNumber = (n) =>
    n !== undefined && n !== null && !isNaN(Number(n))
      ? Number(n).toFixed(2)
      : "—";

  const rows = data.map((row) => ({
    REC: row.rec === 1 ? "✔️" : "",
    "Nr Jurnal": row.nrJurnalSpecial || "—",
    SKU: row.sku || "—",
    "Tip Contract": row.tipContract || "—",
    Nume: row.numeSiPrenume || "—",
    Summary: row.summary || "—",
    "Preț Intrare": formatNumber(row.pretIntrareRON),
    "Data Intrare": formatDate(row.dataIntrare),
  }));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(wb, ws, "Intrari");
  XLSX.writeFile(wb, "Export_Intrari.xlsx");
};

// PDF export function: uses same columns and formatting as Excel
const exportToPDF = (data, meta = {}) => {
  const doc = new jsPDF({ orientation: "landscape", format: "a4" });
  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const formatNumber = (n) =>
    n !== undefined && n !== null && !isNaN(Number(n))
      ? Number(n).toFixed(2)
      : "—";

  const headers = [
    "REC",
    "Nr Jurnal",
    "SKU",
    "Tip Contract",
    "Nume",
    "Summary",
    "Pret Intrare",
    "Data Intrare",
  ].map(toPdfSafeText);
  const normalizeTip = (value) => (value || "").toString().toLowerCase();
  const cnRows = data.filter((row) => {
    const tip = normalizeTip(row.tipContract);
    return tip.includes("consignatie") || tip.includes("consignație");
  });
  const vdRows = data.filter((row) => {
    const tip = normalizeTip(row.tipContract);
    return (
      tip.includes("vanzare directa") ||
      tip.includes("vanzare directă") ||
      tip.includes("vanzare") ||
      tip.includes("vânzare")
    );
  });
  const otherRows = data.filter(
    (row) =>
      !normalizeTip(row.tipContract).includes("consignatie") &&
      !normalizeTip(row.tipContract).includes("consignație") &&
      !normalizeTip(row.tipContract).includes("vanzare directa") &&
      !normalizeTip(row.tipContract).includes("vanzare directă") &&
      !normalizeTip(row.tipContract).includes("vanzare") &&
      !normalizeTip(row.tipContract).includes("vânzare")
  );

  const rows = [];
  const pushGroup = (label, groupRows) => {
    if (!groupRows.length) return;
    rows.push([
      {
        content: label,
        colSpan: headers.length,
        styles: {
          fillColor: [235, 239, 245],
          textColor: 20,
          fontStyle: "bold",
        },
      },
    ]);
    groupRows.forEach((row) => {
      rows.push([
        row.rec === 1 ? "X" : "",
        toPdfSafeText(row.nrJurnalSpecial || "—"),
        toPdfSafeText(row.sku || "—"),
        toPdfSafeText(row.tipContract || "—"),
        toPdfSafeText(row.numeSiPrenume || "—"),
        toPdfSafeText(row.summary || "—"),
        toPdfSafeText(formatNumber(row.pretIntrareRON)),
        toPdfSafeText(formatDate(row.dataIntrare)),
      ]);
    });
  };

  pushGroup("Consignatie", cnRows);
  pushGroup("Vanzare Directa", vdRows);
  pushGroup("Altele", otherRows);

  const titleText = toPdfSafeText(meta.title || "Jurnal Intrari");
  const generatedText = meta.generatedAt
    ? toPdfSafeText(`Generat la: ${meta.generatedAt}`)
    : "";
  const filterText = meta.filterLabel
    ? toPdfSafeText(`Filtru: ${meta.filterLabel}`)
    : "";

  doc.setFontSize(14);
  doc.text(titleText, 14, 14);
  doc.setFontSize(9);
  if (generatedText) doc.text(generatedText, 14, 20);
  if (filterText) doc.text(filterText, 14, 24);

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 30,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [0, 123, 255], textColor: 255 },
  });

  doc.save("Export_Intrari.pdf");
};

function IntrariTab({
  intrariStartDate = null,
  intrariEndDate = null,
  setIntrariStartDate = () => {},
  setIntrariEndDate = () => {},
  handleRefreshTasks = () => {},
  handleRecChangeForIntrari = () => {},
}) {
  const [intrariStartDateLocal, setIntrariStartDateLocal] = useState(null);
  const [intrariEndDateLocal, setIntrariEndDateLocal] = useState(null);
  const [intrariData, setIntrariData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  // State pentru totaluri locale
  const [totalPretIntrare, setTotalPretIntrare] = useState(0);
  const [numarTotalIntrari, setNumarTotalIntrari] = useState(0);
  const [selectedQuickRange, setSelectedQuickRange] = useState("");
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: "asc",
  });
  const [groupByTipContract, setGroupByTipContract] = useState(false);
  const [faraExtFilter, setFaraExtFilter] = useState(false);
  const normalizeText = (value = "") =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  const rowHasExternalStock = (row = {}) => {
    const raw = row.stocExtern ?? row.stoc_extern ?? row.customfield_10337 ?? "";
    let label = "";
    if (raw && typeof raw === "object") {
      label = raw.value || raw.label || raw.name || raw.text || "";
    } else {
      label = raw;
    }
    const normalized = normalizeText(label);
    return (
      normalized === "da" ||
      normalized === "yes" ||
      normalized === "true" ||
      normalized === "1" ||
      normalized.includes(" da")
    );
  };
  // Calculează totalurile când se schimbă filteredData
  useEffect(() => {
    const sumaPretIntrare = filteredData.reduce(
      (acc, curr) => acc + (curr.pretIntrareRON || 0),
      0
    );
    setTotalPretIntrare(sumaPretIntrare);
    setNumarTotalIntrari(filteredData.length);
  }, [filteredData]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("ro-RO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const integerCurrencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("ro-RO", {
        maximumFractionDigits: 0,
      }),
    []
  );

  // Export dialog and export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogText, setExportDialogText] = useState("");
  const [exportLogMessages, setExportLogMessages] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);

  const [isLoadingIntrari, setIsLoadingIntrari] = useState(false);
  const generatedAt = useMemo(() => new Date().toLocaleDateString("ro-RO"), []);
  const filteredRangeLabel = useMemo(() => {
    const extLabel = faraExtFilter ? " + Fără EXT" : "";
    if (!intrariStartDateLocal || !intrariEndDateLocal) {
      return `Toate datele${extLabel}`;
    }
    const startLabel = new Date(intrariStartDateLocal).toLocaleDateString(
      "ro-RO"
    );
    const endLabel = new Date(intrariEndDateLocal).toLocaleDateString("ro-RO");
    return `${startLabel} - ${endLabel}${extLabel}`;
  }, [intrariStartDateLocal, intrariEndDateLocal, faraExtFilter]);

  const handleRangeChange = (dates) => {
    const [start, end] = dates;
    setIntrariStartDateLocal(start ? startOfDay(start) : null);
    setIntrariEndDateLocal(end ? endOfDay(end) : null);
    setSelectedQuickRange("");
  };

  const handleQuickRangeSelect = (presetValue) => {
    const today = new Date();
    let start = startOfDay(today);
    let end = endOfDay(today);

    switch (presetValue) {
      case "last7":
        start = startOfDay(new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000));
        break;
      case "last30":
        start = startOfDay(
          new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000)
        );
        break;
      case "thisMonth":
        start = startOfDay(new Date(today.getFullYear(), today.getMonth(), 1));
        end = endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0));
        break;
      default:
        break;
    }

    setIntrariStartDateLocal(start);
    setIntrariEndDateLocal(end);
    setSelectedQuickRange(presetValue);
  };

  const handleClearRange = () => {
    setIntrariStartDateLocal(null);
    setIntrariEndDateLocal(null);
    setSelectedQuickRange("");
    setFaraExtFilter(false);
  };

  const handleSort = (columnId) => {
    setSortConfig((prev) => {
      if (prev.field === columnId) {
        return {
          field: columnId,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { field: columnId, direction: "asc" };
    });
  };

  const formatPretIntrare = (value) => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
      return "—";
    }
    return currencyFormatter.format(numberValue);
  };

  const isRangeSelected = Boolean(
    intrariStartDateLocal ||
      intrariEndDateLocal ||
      selectedQuickRange ||
      faraExtFilter
  );

  const sortedData = useMemo(() => {
    if (!Array.isArray(filteredData)) {
      return [];
    }
    if (!sortConfig.field) {
      return filteredData;
    }
    const getter = COLUMN_VALUE_GETTERS[sortConfig.field];
    if (!getter) {
      return filteredData;
    }
    const dataCopy = [...filteredData];
    dataCopy.sort((a, b) => {
      const valueA = getter(a);
      const valueB = getter(b);

      if (valueA === null || valueA === undefined) {
        return valueB === null || valueB === undefined
          ? 0
          : sortConfig.direction === "asc"
            ? 1
            : -1;
      }
      if (valueB === null || valueB === undefined) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }

      if (typeof valueA === "number" && typeof valueB === "number") {
        return sortConfig.direction === "asc"
          ? valueA - valueB
          : valueB - valueA;
      }

      const comparison = valueA
        .toString()
        .localeCompare(valueB.toString(), "ro", {
          numeric: true,
          sensitivity: "base",
        });

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
    return dataCopy;
  }, [filteredData, sortConfig]);

  const groupedSortedData = useMemo(() => {
    if (!Array.isArray(sortedData)) {
      return [];
    }
    const normalizeTip = (value) => (value || "").toString().toLowerCase();
    const cnRows = [];
    const vdRows = [];
    const otherRows = [];
    sortedData.forEach((row) => {
      const tip = normalizeTip(row.tipContract);
      if (tip.includes("consignatie") || tip.includes("consignație")) {
        cnRows.push(row);
      } else if (
        tip.includes("vanzare directa") ||
        tip.includes("vanzare directă") ||
        tip.includes("vanzare") ||
        tip.includes("vânzare")
      ) {
        vdRows.push(row);
      } else {
        otherRows.push(row);
      }
    });
    return [...cnRows, ...vdRows, ...otherRows];
  }, [sortedData]);

  const groupedRows = useMemo(() => {
    const normalizeTip = (value) => (value || "").toString().toLowerCase();
    const cnRows = groupedSortedData.filter((row) => {
      const tip = normalizeTip(row.tipContract);
      return tip.includes("consignatie") || tip.includes("consignație");
    });
    const vdRows = groupedSortedData.filter((row) => {
      const tip = normalizeTip(row.tipContract);
      return (
        tip.includes("vanzare directa") ||
        tip.includes("vanzare directă") ||
        tip.includes("vanzare") ||
        tip.includes("vânzare")
      );
    });
    const otherRows = groupedSortedData.filter((row) => {
      const tip = normalizeTip(row.tipContract);
      return (
        !tip.includes("consignatie") &&
        !tip.includes("consignație") &&
        !tip.includes("vanzare directa") &&
        !tip.includes("vanzare directă") &&
        !tip.includes("vanzare") &&
        !tip.includes("vânzare")
      );
    });
    const rows = [];
    if (cnRows.length)
      rows.push(
        { type: "group", label: "Consignatie" },
        ...cnRows.map((row) => ({ type: "row", row }))
      );
    if (vdRows.length)
      rows.push(
        { type: "group", label: "Vanzare Directa" },
        ...vdRows.map((row) => ({ type: "row", row }))
      );
    if (otherRows.length)
      rows.push(
        { type: "group", label: "Altele" },
        ...otherRows.map((row) => ({ type: "row", row }))
      );
    return rows;
  }, [groupedSortedData]);

  const actionButtonStyles = {
    bgcolor: "rgba(255,255,255,0.18)",
    color: "#fff",
    textTransform: "none",
    borderRadius: 3,
    px: 2.5,
    "&:hover": {
      bgcolor: "rgba(255,255,255,0.32)",
    },
  };

  useEffect(() => {
    const fetchIntrariData = async () => {
      try {
        setIsLoadingIntrari(true);
        const response = await axios.get(
          "https://server-9p4hx.kinsta.app/api/jurnale/intrari",
          {
            params: faraExtFilter ? { faraExt: 1 } : {},
          }
        );
        setIntrariData(response.data);
        setFilteredData(response.data);
      } catch (error) {
        console.error("Eroare la preluarea datelor de intrări:", error);
      } finally {
        setIsLoadingIntrari(false);
      }
    };
    fetchIntrariData();
  }, [faraExtFilter]);

  useEffect(() => {
    if (!intrariStartDateLocal || !intrariEndDateLocal) {
      const base = faraExtFilter
        ? intrariData.filter((item) => !rowHasExternalStock(item))
        : intrariData;
      setFilteredData(base);
      return;
    }

    const filtered = intrariData.filter((item) => {
      if (faraExtFilter && rowHasExternalStock(item)) return false;
      const itemDate = item.dataIntrare ? new Date(item.dataIntrare) : null;
      return (
        itemDate &&
        itemDate >= intrariStartDateLocal &&
        itemDate <= intrariEndDateLocal
      );
    });

    setFilteredData(filtered);
  }, [intrariStartDateLocal, intrariEndDateLocal, intrariData, faraExtFilter]);

  // Export Zip logic
  const handleExportZip = async () => {
    setExportDialogText("Pregătim arhiva pentru export...");
    setExportDialogOpen(true);
    setIsSyncing(true);
    setExportLogMessages([]);
    const addLogMessage = (msg) =>
      setExportLogMessages((prev) => [...prev, msg]);

    try {
      const dataForExport =
        Array.isArray(groupedSortedData) && groupedSortedData.length > 0
          ? groupedSortedData
          : filteredData;

      if (!dataForExport || dataForExport.length === 0) {
        alert("Nu există date disponibile pentru export.");
        return;
      }

      const columns = [
        { label: "Nr Jurnal", field: "nrJurnalSpecial" },
        { label: "SKU", field: "sku" },
        { label: "Tip Contract", field: "tipContract" },
        { label: "Nume", field: "numeSiPrenume" },
        { label: "Summary", field: "summary" },
        { label: "Pret Intrare", field: "pretIntrareRON" },
        { label: "Data Intrare", field: "dataIntrare" },
      ];

      const rawData = dataForExport.map((row) => {
        const obj = {};
        columns.forEach((col) => {
          obj[col.field] = row[col.field] ?? "";
        });
        return obj;
      });

      const fileName = `Export Intrari`;
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(rawData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      const excelBlob = new Blob(
        [XLSX.write(workbook, { bookType: "xlsx", type: "array" })],
        { type: "application/octet-stream" }
      );

      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text(toPdfSafeText("Jurnal Intrari"), 14, 14);
      doc.setFontSize(9);
      doc.text(toPdfSafeText(`Generat la: ${generatedAt}`), 14, 20);
      doc.text(toPdfSafeText(`Filtru: ${filteredRangeLabel}`), 14, 24);
      const headers = [[...columns.map((col) => toPdfSafeText(col.label))]];
      const body = rawData.map((row) =>
        columns.map((col) => toPdfSafeText(row[col.field] || ""))
      );
      autoTable(doc, { head: headers, body, startY: 30 });
      const pdfBlob = doc.output("blob");

      const toBase64 = (blob) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => resolve(reader.result.split(",")[1]);
          reader.onerror = reject;
        });

      const [excelBase64, pdfBase64] = await Promise.all([
        toBase64(excelBlob),
        toBase64(pdfBlob),
      ]);

      const tasks = dataForExport.map((row) => ({
        issue_id: row.issue_id,
        nr_jurnal_special: row.nrJurnalSpecial,
        nume_si_prenume: row.numeSiPrenume,
        tip_achizitie: row.tip_achizitie || row.tipContract || "",
        attachments: row.attachments || "",
      }));

      const tip_achizitie = tasks[0]?.tip_achizitie || "Necunoscut";
      const dataSample = dataForExport[0]?.dataIntrare;
      const luna = dataSample
        ? new Date(dataSample).toLocaleString("ro-RO", {
            month: "long",
            year: "numeric",
          })
        : "Luna-Neidentificata";

      const payload = {
        tip_achizitie,
        luna,
        tasks,
        rawData,
        columns,
        fileName,
        pdfBuffer: pdfBase64,
        excelBuffer: excelBase64,
      };

      addLogMessage("Trimitere date către server...");
      setExportDialogText("Trimitere date către server...");

      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/tasks/export-zip-server",
        payload,
        { responseType: "blob" }
      );

      if (response && response.data) {
        addLogMessage("Descărcare ZIP de pe server...");
        setExportDialogText("Descărcare ZIP de pe server...");
        const blob = new Blob([response.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Export_Contracte.zip");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setExportDialogText("Export finalizat cu succes!");
        setTimeout(() => setExportDialogOpen(false), 2500);
      } else {
        throw new Error(
          "Răspunsul serverului nu conține date pentru descărcare ZIP."
        );
      }
    } catch (err) {
      console.error("Eroare la exportul Zip:", err);
      setExportDialogText("Eroare la exportul Zip: " + (err.message || err));
      setTimeout(() => setExportDialogOpen(false), 3500);
      alert("Eroare la exportul Zip: " + (err.message || err));
    } finally {
      setIsSyncing(false);
    }
  };

  // REC checkbox update logic
  const handleRecChange = async (issue_id, newRecValue) => {
    try {
      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/jurnale/update-rec",
        {
          issue_id,
          rec: newRecValue ? 1 : 0,
        }
      );

      if (response.status === 200) {
        const updated = filteredData.map((row) =>
          row.issue_id === issue_id ? { ...row, rec: newRecValue ? 1 : 0 } : row
        );
        setFilteredData(updated);
      }
    } catch (error) {
      console.error("Eroare la actualizarea valorii REC:", error);
    }
  };

  // Local handler for Excel export
  const handleExportToExcel = () => {
    exportToExcel(groupedSortedData);
  };

  // Local handler for PDF export
  const handleExportToPDF = () => {
    exportToPDF(groupByTipContract ? groupedSortedData : sortedData, {
      title: "Jurnal Intrari",
      generatedAt,
      filterLabel: filteredRangeLabel,
    });
  };

  const tableRows = groupByTipContract
    ? groupedRows
    : Array.isArray(sortedData)
      ? sortedData
      : [];

  return (
    <>
      <Paper
        sx={{
          p: 3,
          borderRadius: 4,
          mb: 3,
          background: "linear-gradient(135deg, #1d2671 0%, #c33764 100%)",
          color: "common.white",
          boxShadow: "0px 25px 60px rgba(12, 19, 79, 0.35)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Jurnal Intrari
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              Generat la: {generatedAt}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Filtru: {filteredRangeLabel}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            flexWrap="wrap"
            justifyContent="flex-end"
            width={{ xs: "100%", md: "auto" }}
          >
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={handleExportToExcel}
              sx={actionButtonStyles}
            >
              Export Excel
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={handleExportToPDF}
              sx={actionButtonStyles}
            >
              Export PDF
            </Button>
            <Button
              variant={groupByTipContract ? "contained" : "outlined"}
              onClick={() => setGroupByTipContract((prev) => !prev)}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                px: 2.5,
                bgcolor: groupByTipContract
                  ? "rgba(255,255,255,0.25)"
                  : "transparent",
                color: "common.white",
                borderColor: "rgba(255,255,255,0.4)",
                "&:hover": {
                  bgcolor: groupByTipContract
                    ? "rgba(255,255,255,0.32)"
                    : "rgba(255,255,255,0.18)",
                  borderColor: "rgba(255,255,255,0.6)",
                },
              }}
            >
              {groupByTipContract ? "CN/VD activ" : "Separă CN/VD"}
            </Button>
            <Button
              variant={faraExtFilter ? "contained" : "outlined"}
              onClick={() => setFaraExtFilter((prev) => !prev)}
              sx={{
                textTransform: "none",
                borderRadius: 3,
                px: 2.5,
                bgcolor: faraExtFilter
                  ? "rgba(255,255,255,0.25)"
                  : "transparent",
                color: "common.white",
                borderColor: "rgba(255,255,255,0.4)",
                "&:hover": {
                  bgcolor: faraExtFilter
                    ? "rgba(255,255,255,0.32)"
                    : "rgba(255,255,255,0.18)",
                  borderColor: "rgba(255,255,255,0.6)",
                },
              }}
            >
              {faraExtFilter ? "Fără EXT activ" : "Fără EXT"}
            </Button>
            <Button
              startIcon={<CloudDownloadIcon />}
              onClick={handleExportZip}
              sx={actionButtonStyles}
            >
              Export Zip
            </Button>
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefreshTasks}
              sx={{ ...actionButtonStyles, bgcolor: "rgba(0,0,0,0.25)" }}
            >
              Reîncarcă Intrările
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 4, mb: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Box>
              <Typography variant="h6">Filtrează după perioadă</Typography>
              <Typography variant="body2" color="text.secondary">
                Selectează un interval sau folosește un preset pentru a rafina
                rapid datele.
              </Typography>
            </Box>
            <Button
              variant="text"
              onClick={handleClearRange}
              disabled={!isRangeSelected}
            >
              Resetează filtrele
            </Button>
          </Stack>
          <Box sx={{ width: { xs: "100%", sm: 360 } }}>
            <DatePicker
              selectsRange
              startDate={intrariStartDateLocal}
              endDate={intrariEndDateLocal}
              onChange={handleRangeChange}
              dateFormat="dd MMM yyyy"
              shouldCloseOnSelect={false}
              customInput={
                <DateRangeInput placeholder="Selectează intervalul dorit" />
              }
            />
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {QUICK_RANGE_PRESETS.map((preset) => (
              <Chip
                key={preset.value}
                label={preset.label}
                onClick={() => handleQuickRangeSelect(preset.value)}
                color={
                  selectedQuickRange === preset.value ? "primary" : "default"
                }
                variant={
                  selectedQuickRange === preset.value ? "filled" : "outlined"
                }
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2.5,
              background: "linear-gradient(135deg, #f7971e, #ffd200)",
              color: "common.white",
              boxShadow: "0px 18px 45px rgba(247, 151, 30, 0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <AttachMoneyIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Total Achiziții
                </Typography>
                <Typography variant="h4" fontWeight={600} lineHeight={1.2}>
                  {`${integerCurrencyFormatter.format(totalPretIntrare)} lei`}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              borderRadius: 4,
              p: 2.5,
              background: "linear-gradient(135deg, #1cb5e0, #000851)",
              color: "common.white",
              boxShadow: "0px 18px 45px rgba(28, 181, 224, 0.35)",
            }}
          >
            <CardContent sx={{ display: "flex", alignItems: "center", p: 0 }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  backgroundColor: "rgba(255,255,255,0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <AccountBalanceWalletIcon sx={{ fontSize: 32 }} />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  Total Intrări
                </Typography>
                <Typography variant="h4" fontWeight={600} lineHeight={1.2}>
                  {numarTotalIntrari.toLocaleString("ro-RO")}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  înregistrări
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 4,
          boxShadow: "0px 25px 60px rgba(15, 23, 42, 0.08)",
          overflow: "hidden",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "grey.100" }}>
              {TABLE_COLUMNS.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{ fontWeight: 600 }}
                  align={column.align || "left"}
                >
                  <TableSortLabel
                    active={sortConfig.field === column.id}
                    direction={
                      sortConfig.field === column.id
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingIntrari ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress size={24} />
                    <Typography variant="body2">
                      Se încarcă datele...
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : tableRows.length > 0 ? (
              tableRows.map((item, index) =>
                groupByTipContract && item.type === "group" ? (
                  <TableRow key={`group-${item.label}-${index}`}>
                    <TableCell
                      colSpan={8}
                      sx={{
                        backgroundColor: "grey.100",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {item.label}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow
                    key={
                      (groupByTipContract ? item.row : item).issue_id || index
                    }
                    sx={{
                      "&:nth-of-type(even)": { backgroundColor: "grey.50" },
                      "&:hover": {
                        backgroundColor: "rgba(33, 150, 243, 0.08)",
                      },
                      transition: "background-color 0.3s ease",
                    }}
                  >
                    <TableCell>
                      <Checkbox
                        checked={
                          (groupByTipContract ? item.row : item).rec === 1 ||
                          (groupByTipContract ? item.row : item).rec === "1"
                        }
                        onChange={() =>
                          handleRecChange(
                            (groupByTipContract ? item.row : item).issue_id,
                            !(
                              (groupByTipContract ? item.row : item).rec ===
                                1 ||
                              (groupByTipContract ? item.row : item).rec === "1"
                            )
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {(groupByTipContract ? item.row : item).nrJurnalSpecial ||
                        "—"}
                    </TableCell>
                    <TableCell>
                      {(groupByTipContract ? item.row : item).sku || "—"}
                    </TableCell>
                    <TableCell>
                      {(groupByTipContract ? item.row : item).tipContract ||
                        "—"}
                    </TableCell>
                    <TableCell>
                      {(groupByTipContract ? item.row : item).numeSiPrenume ||
                        "—"}
                    </TableCell>
                    <TableCell>
                      {(groupByTipContract ? item.row : item).summary || "—"}
                    </TableCell>
                    <TableCell align="right">
                      {formatPretIntrare(
                        (groupByTipContract ? item.row : item).pretIntrareRON
                      )}
                    </TableCell>
                    <TableCell>
                      {(groupByTipContract ? item.row : item).dataIntrare
                        ? new Date(
                            (groupByTipContract ? item.row : item).dataIntrare
                          ).toLocaleDateString("ro-RO")
                        : "—"}
                    </TableCell>
                  </TableRow>
                )
              )
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Date indisponibile
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isSyncing && (
        <Box sx={{ width: "100%", mt: 3 }}>
          <LinearProgress />
          <Typography variant="body2" align="center" mt={1}>
            Procesăm exportul...
          </Typography>
        </Box>
      )}

      <Dialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      >
        <DialogTitle>Export în curs</DialogTitle>
        <DialogContent>
          <Typography>{exportDialogText}</Typography>
          {exportDialogText === "Export finalizat cu succes!" && (
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
              <Typography variant="h6" color="success.main">
                Export realizat cu succes!
              </Typography>
            </Box>
          )}
          <Box mt={2} sx={{ maxHeight: 200, overflowY: "auto" }}>
            {exportLogMessages.map((msg, idx) => (
              <Typography key={idx} variant="body2">
                • {msg}
              </Typography>
            ))}
          </Box>
          <Box mt={2} display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography variant="body2">
              Exportul este în curs. Te rugăm să aștepți câteva secunde...
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} color="primary">
            Închide
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default IntrariTab;
